import React, { useEffect, useRef } from 'react';

interface AudioWaveformCanvasProps {
	waveformData: number[];
	/** Playback progress from 0 to 1 */
	progress?: number;
	/** Called with click position as 0-1 fraction */
	onClick?: (percent: number) => void;
	activeColor: string;
	inactiveColor: string;
	progressLineColor?: string;
	width?: number;
	height?: number;
	className?: string;
}

const AudioWaveformCanvas: React.FC<AudioWaveformCanvasProps> = ({
	waveformData,
	progress,
	onClick,
	activeColor,
	inactiveColor,
	progressLineColor,
	width = 200,
	height = 40,
	className,
}) => {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);

	useEffect(() => {
		if (!canvasRef.current || waveformData.length === 0) return;

		const canvas = canvasRef.current;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		const w = canvas.width;
		const h = canvas.height;
		const barWidth = w / waveformData.length;
		const progressX = progress != null ? w * progress : -1;

		ctx.clearRect(0, 0, w, h);

		waveformData.forEach((value, index) => {
			const x = index * barWidth;
			const barHeight = (value / 100) * h;
			const y = (h - barHeight) / 2;

			if (progressX >= 0 && x < progressX) {
				ctx.fillStyle = activeColor;
			} else {
				ctx.fillStyle = inactiveColor;
			}

			ctx.fillRect(x, y, barWidth - 2, barHeight);
		});

		// Draw progress line
		if (progressX > 0 && progressX < w) {
			const lineColor = progressLineColor || activeColor;
			ctx.strokeStyle = lineColor;
			ctx.lineWidth = 2;
			ctx.beginPath();
			ctx.moveTo(progressX, 0);
			ctx.lineTo(progressX, h);
			ctx.stroke();
		}
	}, [waveformData, progress, activeColor, inactiveColor, progressLineColor, width, height]);

	const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
		if (!onClick || !canvasRef.current) return;
		const rect = canvasRef.current.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const percent = x / rect.width;
		onClick(percent);
	};

	return (
		<canvas
			ref={canvasRef}
			width={width}
			height={height}
			onClick={onClick ? handleClick : undefined}
			style={{ maxWidth: width + 'px' }}
			className={
				'block h-full w-full' +
				(onClick ? ' cursor-pointer' : '') +
				(className ? ' ' + className : '')
			}
		/>
	);
};

export default AudioWaveformCanvas;
